import React from 'react';
import { InteractionManager, Platform } from 'react-native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeNavigation, useSafeRouter } from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import type {
  AppStackParamList,
  ModalStackParamList,
} from '../../../../navigation/AppNavigation.props';
import { logger } from '../utils';

// https://sweetgreen.atlassian.net/wiki/spaces/DES/pages/4469325825/Order+-+Reorder+Frontend+Flow
export const useReorderNavigation = () => {
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const router = useSafeRouter();

  const navigation =
    useSafeNavigation<NativeStackNavigationProp<AppStackParamList>>();
  const modalNavigation =
    useSafeNavigation<NativeStackNavigationProp<ModalStackParamList>>();

  // ─── Navigate To Modal Stack ────────────────────────────────────────────────

  const navigateToActiveBagWarning = React.useCallback(() => {
    logger.info('Navigate to ReorderActiveBagWarning');

    if (isExpoRouterEnabled) {
      router?.navigate('/reorder-active-bag-warning');
      return;
    }

    navigation?.navigate('Modal', { screen: 'ReorderActiveBagWarning' });
  }, [isExpoRouterEnabled, router, navigation]);

  const navigateToConfirmLocation = React.useCallback(() => {
    logger.info('Navigate to ReorderConfirmLocation');

    if (isExpoRouterEnabled) {
      router?.navigate('/reorder-confirm-location');
      return;
    }

    navigation?.navigate('Modal', { screen: 'ReorderConfirmLocation' });
  }, [isExpoRouterEnabled, router, navigation]);

  const navigateToChangeLocation = React.useCallback(() => {
    logger.info('Navigate to ReorderChangeLocation');

    if (isExpoRouterEnabled) {
      router?.navigate('/reorder-change-location');
      return;
    }

    navigation?.navigate('Modal', { screen: 'ReorderChangeLocation' });
  }, [isExpoRouterEnabled, router, navigation]);

  const navigateToConflictReview = React.useCallback(() => {
    logger.info('Navigate to ReorderConflictReview');

    if (isExpoRouterEnabled) {
      router?.navigate('/reorder-conflict-review');
      return;
    }

    navigation?.navigate('Modal', { screen: 'ReorderConflictReview' });
  }, [isExpoRouterEnabled, router, navigation]);

  // ─── Replace Current Route In Modal Stack ───────────────────────────────────

  const replaceWithConfirmLocation = React.useCallback(() => {
    logger.info('Replace with ReorderConfirmLocation');

    if (isExpoRouterEnabled) {
      router?.replace('/reorder-confirm-location');
      return;
    }

    modalNavigation?.replace('ReorderConfirmLocation');
  }, [isExpoRouterEnabled, router, modalNavigation]);

  const replaceWithChangeLocation = React.useCallback(() => {
    logger.info('Replace with ReorderChangeLocation');

    if (isExpoRouterEnabled) {
      router?.replace('/reorder-change-location');
      return;
    }

    modalNavigation?.replace('ReorderChangeLocation');
  }, [isExpoRouterEnabled, router, modalNavigation]);

  const replaceWithConflictReview = React.useCallback(() => {
    logger.info('Replace with ReorderConflictReview');

    if (isExpoRouterEnabled) {
      router?.replace('/reorder-conflict-review');
      return;
    }

    modalNavigation?.replace('ReorderConflictReview');
  }, [isExpoRouterEnabled, router, modalNavigation]);

  // ─── Dismiss / Finish ───────────────────────────────────────────────────────

  const dismissReorder = React.useCallback(() => {
    logger.info('Dismiss Reorder');

    if (isExpoRouterEnabled) {
      if (router?.canDismiss()) router.dismiss();
      else router?.back();
      return;
    }

    if (navigation?.canGoBack()) navigation.goBack();
    else navigation?.getParent()?.goBack();
  }, [isExpoRouterEnabled, router, navigation]);

  const finishReorder = React.useCallback(
    (restaurantSlug?: string, addressId?: string) => {
      logger.info('Reset To Bag', restaurantSlug, addressId);

      if (isExpoRouterEnabled) {
        if (router?.canDismiss()) router.dismissAll();
        navigateToMenuExpoRouter(router, restaurantSlug, addressId);
        router?.navigate('/bag');
        return;
      }

      if (navigation) {
        resetNavStackAfterInteractions({ navigation, restaurantSlug, addressId, withBag: true }); // prettier-ignore
      }
    },
    [isExpoRouterEnabled, router, navigation],
  );

  const dismissReorderNewMenu = React.useCallback(
    (restaurantSlug?: string, addressId?: string) => {
      logger.info('Reset to Menu', restaurantSlug, addressId);

      if (isExpoRouterEnabled) {
        if (router?.canDismiss()) router.dismissAll();
        navigateToMenuExpoRouter(router, restaurantSlug, addressId);
        return;
      }

      if (navigation) {
        resetNavStackAfterInteractions({ navigation, restaurantSlug, addressId, withBag: false }); // prettier-ignore
      }
    },
    [isExpoRouterEnabled, router, navigation],
  );

  const dismissReorderHome = React.useCallback(() => {
    if (isExpoRouterEnabled) {
      router?.dismissTo('/');
      return;
    }

    navigation?.navigate('MainTabs', {
      screen: 'HomeTab',
      params: { screen: 'Home' },
    });
  }, [isExpoRouterEnabled, router, navigation]);

  return {
    navigateToActiveBagWarning,
    navigateToConfirmLocation,
    navigateToChangeLocation,
    navigateToConflictReview,
    replaceWithConfirmLocation,
    replaceWithChangeLocation,
    replaceWithConflictReview,
    dismissReorder,
    finishReorder,
    dismissReorderNewMenu,
    dismissReorderHome,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function navigateToMenuExpoRouter(
  router: ReturnType<typeof useSafeRouter>,
  restaurantSlug?: string,
  addressId?: string,
) {
  if (addressId) {
    router?.replace({ pathname: '/delivery/[addressId]/menu', params: { addressId } }); // prettier-ignore
    return;
  }

  if (restaurantSlug) {
    router?.replace({ pathname: '/[restaurantSlug]/menu', params: { restaurantSlug } }); // prettier-ignore
    return;
  }

  router?.replace('/locations');
}

/**
 * Resets the navigation stack to a new menu, optionally with the bag on top.
 *
 * NOTE: Uses `InteractionManager` on native to ensure the stack is reset after
 *       any pending interactions.
 */
function resetNavStackAfterInteractions(props: ResetStackParams) {
  //
  // ─── Web ─────────────────────────────────────────────────────────────

  if (Platform.OS === 'web') {
    resetNavStack(props);

    return;
  }

  // ─── Native ──────────────────────────────────────────────────────────

  InteractionManager.runAfterInteractions(() => resetNavStack(props));
}

function resetNavStack(props: ResetStackParams) {
  const { navigation, restaurantSlug, addressId, withBag } = props;

  navigation.reset({
    routes: [
      {
        name: 'MainTabs',
        params: {
          screen: 'MenuTab',
          params: addressId
            ? { screen: 'DeliveryMenu', params: { addressId } }
            : { screen: 'Menu', params: { restaurantSlug } },
        } as const,
      },
      ...(withBag
        ? [
            {
              name: 'Modal',
              params: { screen: 'Bag' },
            } as const,
          ]
        : []),
    ],
  });
}

// ─── Types ───────────────────────────────────────────────────────────────────

type ResetStackParams = {
  navigation: NativeStackNavigationProp<AppStackParamList>;
  restaurantSlug?: string;
  addressId?: string;
  withBag?: boolean;
};
